import { useState, useEffect } from 'react';
import {
  RotateCcw,
  AlertTriangle,
  Loader2,
  ShieldAlert,
  Banknote,
  CreditCard,
  DollarSign,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ManagerPinDialog } from '@/components/ManagerPinDialog';
import { useCurrency } from '@/hooks/useCurrency';
import { useOwner2FAApproval } from '@/hooks/useOwner2FAApproval';
import { cn } from '@/lib/utils';

interface Order {
  id: string;
  order_number: string;
  total: number;
  status: string;
  payments?: Array<{
    id: string;
    payment_method: string;
    amount: number;
  }>;
}

interface RefundOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: Order | null;
  onRefundOrder: (data: {
    orderId: string;
    amount: number;
    reason: string;
    refundMethod: string;
    approvedBy: string;
  }) => Promise<void>;
  isProcessing?: boolean;
}

const refundReasons = [
  'Customer complaint',
  'Wrong item delivered',
  'Quality issue',
  'Late delivery',
  'Customer changed mind',
  'Overcharged',
  'Other',
];

type RefundType = 'full' | 'partial';
type RefundMethod = 'cash' | 'card' | 'original';

export function RefundOrderDialog({
  open,
  onOpenChange,
  order,
  onRefundOrder,
  isProcessing = false,
}: RefundOrderDialogProps) {
  const { formatCurrency } = useCurrency();
  const { requireApproval, owner2FAEnabled } = useOwner2FAApproval();
  const [refundType, setRefundType] = useState<RefundType>('full');
  const [partialAmount, setPartialAmount] = useState('');
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [refundMethod, setRefundMethod] = useState<RefundMethod>('original');
  const [error, setError] = useState<string | null>(null);
  const [showPinDialog, setShowPinDialog] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setRefundType('full');
      setPartialAmount('');
      setReason('');
      setCustomReason('');
      setRefundMethod('original');
      setError(null);
    }
  }, [open]);

  const handleOpen = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  const getRefundAmount = (): number => {
    if (!order) return 0;
    if (refundType === 'full') return order.total;
    return parseFloat(partialAmount) || 0;
  };

  const validateRefund = (): string | null => {
    const finalReason = reason === 'Other' ? customReason : reason;

    if (!finalReason.trim()) {
      return 'Please select or enter a reason for the refund';
    }

    const amount = getRefundAmount();
    if (amount <= 0) {
      return 'Refund amount must be greater than zero';
    }

    if (order && amount > order.total) {
      return 'Refund amount cannot exceed order total';
    }

    return null;
  };

  const handleInitiateRefund = () => {
    const validationError = validateRefund();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setShowPinDialog(true);
  };

  const executeRefund = async (managerName: string) => {
    if (!order) return;

    const finalReason = reason === 'Other' ? customReason : reason;
    const amount = getRefundAmount();

    try {
      await onRefundOrder({
        orderId: order.id,
        amount,
        reason: finalReason,
        refundMethod,
        approvedBy: managerName,
      });
      handleOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to process refund');
    }
  };

  const handlePinVerified = async (managerId: string, managerName: string) => {
    if (!order) return;

    const amount = getRefundAmount();
    const isFullRefund = refundType === 'full';

    // Check if owner 2FA is required for this refund
    if (owner2FAEnabled) {
      const approved = await requireApproval({
        actionType: 'refund',
        metadata: {
          orderId: order.id,
          orderNumber: order.order_number,
          amount,
          isFullRefund,
          reason: reason === 'Other' ? customReason : reason,
          refundMethod,
          managerId,
          managerName,
        },
        description: `${isFullRefund ? 'Full' : 'Partial'} Refund - Order #${order.order_number} (${formatCurrency(amount)})`,
        amount,
      });

      if (approved) {
        await executeRefund(managerName);
      } else {
        setError('Owner 2FA approval denied');
      }
    } else {
      // No owner 2FA required, proceed directly
      await executeRefund(managerName);
    }
  };

  // Determine original payment method
  const originalPaymentMethod = order?.payments?.[0]?.payment_method || 'cash';

  if (!order) return null;

  return (
    <>
      <ResponsiveModal open={open} onOpenChange={handleOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <RotateCcw className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <ResponsiveModalTitle>Refund Order</ResponsiveModalTitle>
                <ResponsiveModalDescription>
                  Order #{order.order_number} • {formatCurrency(order.total)}
                </ResponsiveModalDescription>
              </div>
            </div>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="space-y-6">
            {/* Warning Banner */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  This action requires manager approval
                </p>
                <p className="text-amber-700 dark:text-amber-300 mt-1">
                  Refunds will be recorded and affect sales reports. This order was already completed.
                </p>
              </div>
            </div>

            {/* Refund Type Selection */}
            <div>
              <Label className="text-sm font-medium mb-3 block">
                Refund Amount
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setRefundType('full')}
                  className={cn(
                    'p-4 text-left rounded-lg border-2 transition-all',
                    refundType === 'full'
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30'
                      : 'border-border hover:border-amber-200'
                  )}
                >
                  <div className="font-medium">Full Refund</div>
                  <div className="text-2xl font-bold text-amber-600 mt-1">
                    {formatCurrency(order.total)}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setRefundType('partial')}
                  className={cn(
                    'p-4 text-left rounded-lg border-2 transition-all',
                    refundType === 'partial'
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30'
                      : 'border-border hover:border-amber-200'
                  )}
                >
                  <div className="font-medium">Partial Refund</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Specify custom amount
                  </div>
                </button>
              </div>
            </div>

            {/* Partial Amount Input */}
            {refundType === 'partial' && (
              <div>
                <Label htmlFor="partial-amount" className="text-sm font-medium">
                  Refund Amount <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1.5">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="partial-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={order.total}
                    value={partialAmount}
                    onChange={(e) => setPartialAmount(e.target.value)}
                    placeholder="0.00"
                    className="pl-9"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum: {formatCurrency(order.total)}
                </p>
              </div>
            )}

            {/* Refund Method Selection */}
            <div>
              <Label className="text-sm font-medium mb-3 block">
                Refund Method
              </Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setRefundMethod('original')}
                  className={cn(
                    'p-3 flex flex-col items-center gap-2 rounded-lg border-2 transition-all',
                    refundMethod === 'original'
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30'
                      : 'border-border hover:border-amber-200'
                  )}
                >
                  {originalPaymentMethod === 'cash' ? (
                    <Banknote className="h-5 w-5" />
                  ) : (
                    <CreditCard className="h-5 w-5" />
                  )}
                  <span className="text-xs font-medium">Original Method</span>
                  <span className="text-xs text-muted-foreground capitalize">
                    ({originalPaymentMethod})
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setRefundMethod('cash')}
                  className={cn(
                    'p-3 flex flex-col items-center gap-2 rounded-lg border-2 transition-all',
                    refundMethod === 'cash'
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30'
                      : 'border-border hover:border-amber-200'
                  )}
                >
                  <Banknote className="h-5 w-5" />
                  <span className="text-xs font-medium">Cash</span>
                </button>
                <button
                  type="button"
                  onClick={() => setRefundMethod('card')}
                  className={cn(
                    'p-3 flex flex-col items-center gap-2 rounded-lg border-2 transition-all',
                    refundMethod === 'card'
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30'
                      : 'border-border hover:border-amber-200'
                  )}
                >
                  <CreditCard className="h-5 w-5" />
                  <span className="text-xs font-medium">Card</span>
                </button>
              </div>
            </div>

            {/* Reason Selection */}
            <div>
              <Label className="text-sm font-medium mb-3 block">
                Reason for Refund <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {refundReasons.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setReason(r)}
                    className={cn(
                      'p-3 text-sm text-left rounded-lg border-2 transition-all',
                      reason === r
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300'
                        : 'border-border hover:border-amber-200 hover:bg-amber-50/50 dark:hover:bg-amber-950/10'
                    )}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Reason */}
            {reason === 'Other' && (
              <div>
                <Label htmlFor="custom-reason" className="text-sm font-medium">
                  Please specify <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="custom-reason"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Enter reason for refund..."
                  className="mt-1.5"
                  rows={3}
                />
              </div>
            )}

            {/* Refund Impact Summary */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <ShieldAlert className="h-4 w-4" />
                Refund Summary:
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>
                  Refund amount:{' '}
                  <span className="font-medium text-foreground">
                    {formatCurrency(getRefundAmount())}
                  </span>
                </li>
                <li>
                  Method:{' '}
                  <span className="font-medium text-foreground capitalize">
                    {refundMethod === 'original' ? originalPaymentMethod : refundMethod}
                  </span>
                </li>
                <li>Order status will be updated to "refunded"</li>
                <li>Refund will appear on settlement report</li>
                <li>Action will be logged to audit trail</li>
              </ul>
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 p-3 rounded-lg">
                {error}
              </p>
            )}
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button
              variant="outline"
              onClick={() => handleOpen(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={handleInitiateRefund}
              disabled={
                isProcessing ||
                !reason ||
                (reason === 'Other' && !customReason.trim()) ||
                (refundType === 'partial' && (!partialAmount || parseFloat(partialAmount) <= 0))
              }
              className="gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4" />
                  Process Refund
                </>
              )}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Manager PIN Dialog */}
      <ManagerPinDialog
        open={showPinDialog}
        onOpenChange={setShowPinDialog}
        onVerified={handlePinVerified}
        title="Manager Approval Required"
        description="Enter manager PIN to process this refund"
      />
    </>
  );
}
